// Mục Kho (kho-erp v1, CEO duyệt bản mẫu 01/10/2026). Kho GNW là sự thật duy
// nhất; GNPF chỉ đọc bản sao và gửi lệnh — máy chủ cắt dữ liệu theo vai.
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { useKhoFlag } from "./flag";

export { useKhoFlag };

export type KhoConfig = components["schemas"]["KhoConfig"];
export type KhoOverview = components["schemas"]["KhoOverview"];
export type KhoAsset = components["schemas"]["KhoAsset"];
export type KhoAway = components["schemas"]["KhoAway"];
export type KhoTask = components["schemas"]["KhoTask"];
export type KhoCommand = components["schemas"]["KhoCommand"];

export type KhoAction =
  | "task_create" | "task_cancel" | "task_claim" | "task_start" | "task_done"
  | "loan_extend_request" | "asset_report_damage" | "asset_report_lost"
  | "external_borrow_open" | "external_borrow_close" | "customer_rental_open" | "customer_rental_close";

export const khoKeys = {
  config: ["kho", "config"] as const,
  overview: (branch: string) => ["kho", "overview", branch] as const,
  all: ["kho"] as const,
};

export function useKhoConfig(enabled = true) {
  return useQuery({
    queryKey: khoKeys.config,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/config");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cấu hình mục Kho"));
      return data;
    },
  });
}

/** Nạp công tắc mục Kho (và máy quét mượn/trả) cho menu. */
export function useKhoConfigSync(loggedIn: boolean) {
  const allowed = loggedIn && hasPermission(ACTION.KhoRead);
  const q = useKhoConfig(allowed);
  const setEnabled = useKhoFlag((s) => s.setEnabled);
  const on = allowed && !!q.data?.enabled;
  useEffect(() => setEnabled(on), [on, setEnabled]);
  const scannerQ = useQuery({
    queryKey: ["kho", "scanner"],
    enabled: on,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/scanner");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được công tắc máy quét"));
      return data;
    },
  });
  const setScanner = useKhoFlag((s) => s.setScanner);
  const scannerOn = on && !!scannerQ.data?.enabled;
  useEffect(() => setScanner(scannerOn), [scannerOn, setScanner]);
}

export function useKhoOverview(branch: string, enabled = true) {
  return useQuery({
    queryKey: khoKeys.overview(branch),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/overview", {
        params: { query: branch ? { branch } : {} },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được mục Kho"));
      return data;
    },
    // Lệnh còn chờ kho trả lời thì hỏi lại dày để nút mở khoá sớm.
    // Lệnh đã kẹt (quá 30 phút) thì thôi hỏi dày — người dùng tự Gửi lại / Bỏ.
    refetchInterval: (q) => (q.state.data?.commands.some((c) => c.state === "queued" && !c.stuck) ? 5_000 : 60_000),
  });
}

export function useKhoCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: KhoAction; payload: Record<string, unknown> }) => {
      const { data, error } = await api.POST("/kho/commands", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được yêu cầu sang kho"));
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: khoKeys.all }),
  });
}

/** Lệnh kẹt quá 30 phút: gửi lại nguyên byte / bỏ tại chỗ. */
export function useKhoStuckAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventId: string; op: "resend" | "dismiss" }) => {
      const params = { path: { eventId: input.eventId } };
      const { data, error } =
        input.op === "resend"
          ? await api.POST("/kho/commands/{eventId}/resend", { params })
          : await api.POST("/kho/commands/{eventId}/dismiss", { params });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thực hiện được"));
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: khoKeys.all }),
  });
}
